import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

import { funnelsKey, getFunnels, messageFor } from "./api";
import { OutcomesControls, parseCohort, parseCut, selectedFunnel, useControls } from "./controls";
import { formatCount, funnelName } from "./format";
import { OrdersStamp, OrdersUnavailable } from "./OrderFigures";
import { getFunnelOutcomes, statsOutcomesKey, type OutcomeCut, type OutcomeRow } from "./stats-api";
import {
  formatMoney,
  formatOrders,
  formatRate,
  OUTCOMES_CAPTION,
  outcomeLabel,
  outcomesSummaryLine,
  sortedOutcomes,
  SUPPRESSED_TEXT,
} from "./stats-format";

const numeric = { meta: { align: "right" as const } };

/** A suppressed row shows its label and the privacy text in every figure cell, never a dash or 0. */
function figure(row: OutcomeRow, format: (r: OutcomeRow) => string): string {
  return row.suppressed ? SUPPRESSED_TEXT : format(row);
}

const CUT_HEADER: Record<OutcomeCut, string> = { variant: "Version", source: "Source", goal: "Goal", product: "Product" };

export function outcomeColumns(by: OutcomeCut): ColumnDef<OutcomeRow, unknown>[] {
  return [
    { id: "cut", header: CUT_HEADER[by], accessorFn: (row) => outcomeLabel(by, row) },
    { id: "customers", header: "Customers", accessorFn: (row) => figure(row, (r) => formatOrders(r.customers)), ...numeric },
    { id: "repeat_rate", header: "Repeat rate", accessorFn: (row) => figure(row, (r) => formatRate(r.repeat_rate)), ...numeric },
    {
      id: "orders_per_customer",
      header: "Orders per customer",
      accessorFn: (row) => figure(row, (r) => (r.orders_per_customer == null ? "–" : r.orders_per_customer.toFixed(2))),
      ...numeric,
    },
    { id: "ltv", header: "LTV", accessorFn: (row) => figure(row, (r) => formatMoney(r.ltv_gbp)), ...numeric },
    { id: "ltv_90d", header: "90-day LTV", accessorFn: (row) => figure(row, (r) => formatMoney(r.ltv_90d_gbp)), ...numeric },
    {
      id: "contribution_per_customer",
      header: "Contribution per customer",
      accessorFn: (row) => figure(row, (r) => formatMoney(r.contribution_per_customer)),
      ...numeric,
    },
    { id: "revenue", header: "Revenue", accessorFn: (row) => figure(row, (r) => formatMoney(r.revenue_gbp)), ...numeric },
  ];
}

/**
 * One funnel's customer outcomes (Precision Funnel C9 R4): lifetime value and
 * contribution for people whose first order fell in the cohort window, by the
 * version, source or app segment they first came from. Every figure is the
 * funnel stats API's (I-4); this tab formats and sorts only.
 */
export function OutcomesTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const cohort = parseCohort(controls.values.cohort);
  const by = parseCut(controls.values.by);
  const funnels = useQuery({ queryKey: funnelsKey(controls.days), queryFn: () => getFunnels(controls.days) });
  const list = funnels.data?.data.funnels ?? [];
  const selected = selectedFunnel(controls.funnel, list, false);

  const outcomes = useQuery({
    queryKey: statsOutcomesKey(selected ?? "", by, cohort),
    queryFn: () => getFunnelOutcomes(selected as string, by, cohort),
    enabled: selected !== null,
  });

  if (funnels.isError) {
    return (
      <Alert>
        <AlertTitle>Landing page figures unavailable</AlertTitle>
        <AlertDescription>{messageFor(funnels.error, areaName)}</AlertDescription>
      </Alert>
    );
  }
  if (funnels.isPending) return <Skeleton className="h-96 w-full" />;
  if (selected === null) {
    return <p className="text-sm text-muted-foreground">No landing page has recorded a visit or a sign-up yet.</p>;
  }

  const stats = outcomes.data?.data;
  const rows = stats ? sortedOutcomes(stats.rows) : [];

  return (
    <div className="flex flex-col gap-4">
      <OutcomesControls controls={controls} funnels={list} selected={selected} cohort={cohort} by={by} />
      {outcomes.isError ? (
        <OrdersUnavailable error={outcomes.error} areaName={areaName} />
      ) : (
        <>
          {stats && (
            <p className="text-base text-ks-ink" aria-live="polite">
              {outcomesSummaryLine(stats, funnelName(selected))}
            </p>
          )}
          <OrdersStamp dataAsOf={outcomes.data?.meta.data_as_of} caption={OUTCOMES_CAPTION} />
          <DataTable
            columns={outcomeColumns(by)}
            rows={rows}
            pending={outcomes.isPending}
            page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
            onPageChange={() => {}}
            emptyMessage="No customer's first order came from this funnel in this window yet."
            caption={`Customer outcomes for ${funnelName(selected)}, first orders in the last ${formatCount(cohort)} days`}
          />
        </>
      )}
    </div>
  );
}
